import { isEditable, type MessageKind } from "@ano/shared";
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import {
  ArrowBendUpLeft,
  Quotes,
  Smiley,
  Copy,
  LinkSimple,
  Globe,
  PushPin,
  TerminalWindow,
  PencilSimple,
  Trash,
  Star,
  Eye,
} from "@/components/ui/icons";
import type { SignalType } from "./MessageCommunitySignals";

interface MessageContextMenuProps {
  messageId: string;
  messageKind: string | null | undefined;
  isOwn: boolean;
  isPinned?: boolean;
  /** Discord-shape inline reply — quotes the immediate clicked message
   *  in the channel timeline. Mirror of the hover-bar `onInlineReply`. */
  onInlineReply?: () => void;
  /** Discord-shape thread starter. Mirror of the hover-bar `onCreateThread`. */
  onCreateThread?: () => void;
  onAddReaction: () => void;
  /** Community-curation marks. Toggles the viewer's signal of that type. */
  onSignal?: (type: SignalType) => void;
  onCopyText: () => void;
  onCopyLink: () => void;
  /** Copy the SHAREABLE public deep link (`…/c/<channel>/m/<id>`). Only
   *  supplied for top-level messages in a public, anonymously-viewable
   *  workspace; absent (and the item hidden) everywhere else. Distinct from
   *  `onCopyLink`, which copies the in-app `?message=` link. */
  onCopyPublicLink?: () => void;
  onPin?: () => void;
  onSendToShell?: (messageId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
}

const ICON_PROPS = { size: 14, weight: "Bold" as const };

/**
 * Right-click menu shown via shadcn's `<ContextMenu>` primitive. Caller
 * wraps the trigger; this component renders the `<ContextMenuContent>`
 * and decides which items appear based on the message's kind, ownership,
 * and which optional handlers were supplied.
 *
 * Items lead with a 14px Solar icon for visual scanability — same icon
 * vocabulary as the hover-bar in `MessageActions.tsx` so users can map
 * the two surfaces (Reply, Create thread, Pin, Edit, Delete) at a glance.
 */
export function MessageContextMenu({
  messageId,
  messageKind,
  isOwn,
  isPinned,
  onInlineReply,
  onCreateThread,
  onAddReaction,
  onSignal,
  onCopyText,
  onCopyLink,
  onCopyPublicLink,
  onPin,
  onSendToShell,
  onEdit,
  onDelete,
}: MessageContextMenuProps) {
  const canSendToShell = Boolean(onSendToShell);
  const kind = (messageKind ?? "text") as MessageKind;

  return (
    <ContextMenuContent className="w-56">
      {onInlineReply && (
        <ContextMenuItem onSelect={onInlineReply} className="gap-2">
          <ArrowBendUpLeft {...ICON_PROPS} className="text-muted-foreground" />
          Reply
        </ContextMenuItem>
      )}
      {onCreateThread && (
        <ContextMenuItem onSelect={onCreateThread} className="gap-2">
          <Quotes {...ICON_PROPS} className="text-muted-foreground" />
          Create thread
        </ContextMenuItem>
      )}
      <ContextMenuItem onSelect={onAddReaction} className="gap-2">
        <Smiley {...ICON_PROPS} className="text-muted-foreground" />
        Add Reaction…
      </ContextMenuItem>
      {onSignal && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            onSelect={() => onSignal("helpful")}
            className="gap-2"
          >
            <Star {...ICON_PROPS} className="text-muted-foreground" />
            Mark as Helpful
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={() => onSignal("worth_reading")}
            className="gap-2"
          >
            <Eye {...ICON_PROPS} className="text-muted-foreground" />
            Mark as Worth Reading
          </ContextMenuItem>
        </>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={onCopyText} className="gap-2">
        <Copy {...ICON_PROPS} className="text-muted-foreground" />
        Copy Text
      </ContextMenuItem>
      <ContextMenuItem onSelect={onCopyLink} className="gap-2">
        <LinkSimple {...ICON_PROPS} className="text-muted-foreground" />
        Copy Link to Message
      </ContextMenuItem>
      {onCopyPublicLink && (
        <ContextMenuItem onSelect={onCopyPublicLink} className="gap-2">
          <Globe {...ICON_PROPS} className="text-muted-foreground" />
          Copy public link
        </ContextMenuItem>
      )}
      {onPin && (
        <ContextMenuItem onSelect={onPin} className="gap-2">
          <PushPin
            {...ICON_PROPS}
            className={isPinned ? "text-warning" : "text-muted-foreground"}
          />
          {isPinned ? "Unpin Message" : "Pin Message"}
        </ContextMenuItem>
      )}
      {canSendToShell && (
        <ContextMenuItem
          onSelect={() => onSendToShell!(messageId)}
          className="gap-2"
        >
          <TerminalWindow {...ICON_PROPS} className="text-muted-foreground" />
          Send to Shell
        </ContextMenuItem>
      )}
      {isOwn && (
        <>
          <ContextMenuSeparator />
          {isEditable(kind) && messageKind !== "terminal_output" && (
            <ContextMenuItem onSelect={onEdit} className="gap-2">
              <PencilSimple {...ICON_PROPS} className="text-muted-foreground" />
              Edit Message
            </ContextMenuItem>
          )}
          <ContextMenuItem
            onSelect={onDelete}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash {...ICON_PROPS} className="text-destructive/70" />
            Delete Message…
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}
